/**
 * React Query Client Configuration
 * Centralized configuration for @tanstack/react-query
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Don't retry auth failures. By the time a 401 reaches React Query, the API
 * client has already attempted a silent token refresh and given up (session is
 * truly expired) — retrying would just spin against a dead session. Everything
 * else keeps the normal retry budget.
 */
function retryUnlessAuthError(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean => {
    const status = (error as { apiError?: { status?: number } })?.apiError?.status;
    if (status === 401) return false;
    return failureCount < maxRetries;
  };
}

/**
 * Create and configure the React Query client
 *
 * Configuration optimized for the Protectum application:
 * - 5 minute stale time (matches current polling interval)
 * - 10 minute garbage collection time
 * - Automatic refetch on window focus and reconnect
 * - 3 retries for queries (matches API client retry logic)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data remains fresh for 5 minutes (matches VITE_POLLING_INTERVAL default)
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Unused data stays in cache for 10 minutes before garbage collection
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime in v4)

      // Refetch when browser window regains focus
      refetchOnWindowFocus: true,

      // Refetch when network reconnects
      refetchOnReconnect: true,

      // Retry failed requests, but never a 401 (session already expired — the
      // API client tried to refresh and gave up).
      retry: retryUnlessAuthError(3),

      // Don't refetch immediately on mount if data is fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once, but never a 401.
      retry: retryUnlessAuthError(1),
    },
  },
});
